import { memo, useState } from 'react';
import { Col, Row } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { QuestionType } from '../../../../enum/question-type.enum';
import { useApp } from '../../../../hook/app.hook';
import { I18nKey } from '../../../../i18n/i18n.key';
import { Section, Question } from '../../../../response/test.response';
import { useWindow } from '../../../../util/window-helper';
import IntigerAnsComponent from '../question-response/intiger-ans.component';
import LongAnsComponent from '../question-response/long-ans.component';
import MultipleChoiceComponent from '../question-response/multiple-choice.component';
import SingleChoiceComponent from '../question-response/sigle-choice.component';
import QuestionComponent from '../question.component';
import JeeActionComponent from './jee-action.component';
import JeeSummaryComponent from './jee-summary.component';
import JeeNavigationComponent from './jee-navigation.component';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import './jee-question-holder.css'; // For animation (optional)
import { SecurityManager } from '../../../../security/security-manager';

const JeeQuestionHolderComponent = () => {
  const { testRes } = useApp();
  const { isMobile } = useWindow();
  const { t } = useTranslation();
  const [showRightPanel, setShowRightPanel] = useState(true);

  const question = testRes.sectionList
    .filter((section: Section) => section.active)[0]
    .questionList.filter((question: Question) => question.active)[0];

  const getAnswerView = (question: Question): React.ReactNode => {
    const components: Record<QuestionType, React.ReactNode> = {
      [QuestionType.SINGLE_CHOICE]: <SingleChoiceComponent />,
      [QuestionType.MULTIPLE_CHOICE]: <MultipleChoiceComponent />,
      [QuestionType.INT_ANS]: <IntigerAnsComponent />,
      [QuestionType.RANGE_ANS]: <IntigerAnsComponent />,
      [QuestionType.MATRIX_MATCH]: <SingleChoiceComponent />,
      [QuestionType.LONG_ANSWER]: <LongAnsComponent />,
    };

    return components[question.type] || <p>Question Impl not available</p>;
  };

  const isPreview = SecurityManager.getPreview() == true;

  return (
    <>
      <div className="font-weight-500">
        <Row className="g-0 position-relative" style={{ overflow: 'visible' }}>
          {/* Left Panel */}
          <Col
            xs={12}
            lg={showRightPanel ? 8 : 11}
            className="pe-lg-3"
            style={{ overflow: 'visible' }}
          >
            <div
              style={{
                overflow: 'scroll',
                height: '64vh',
                padding: '6px',
              }}
            >
              <div className={`w-100 ${isMobile ? 'pt-3' : 'pt-4 px-3'}`}>
                <div className={`${isMobile ? '' : 'pb-3'}`}>
                  <div className="d-flex align-items-center border-bottom border-lightgray pb-2">
                    <h6 className="font-weight-600">
                      {t(I18nKey.TEST_QUESTION_TEXT)}
                      <span className="mx-1">{question.index}</span>
                      {isPreview && (
                        <span className="mx-1">({question.counter})</span>
                      )}
                    </h6>
                  </div>

                  <QuestionComponent question={question} />
                </div>
              </div>

              <div className={`w-100 ${isMobile ? 'pt-3' : 'px-3'}`}>
                <div className="d-flex align-items-center border-bottom border-lightgray pb-2">
                  <h6 className="font-weight-600">
                    {t(I18nKey.ENTER_YOUR_RESPONSE_TEXT)}
                  </h6>
                </div>

                <div
                  className={`pt-4 ${
                    isMobile ? 'response--mweb-h' : 'response--h'
                  } response--area`}
                >
                  {getAnswerView(question)}
                </div>
              </div>
            </div>
            <JeeActionComponent />
          </Col>

          {/* Right Panel */}
          <Col
            xs={12}
            lg={showRightPanel ? 4 : 1}
            className={`ps-lg-3 right-panel`}
            style={{ overflow: 'visible' }}
          >
            {/* Toggle Button - Only visible on desktop */}
            <div
              className="toggle-btn"
              onClick={() => setShowRightPanel(!showRightPanel)}
              title={showRightPanel ? 'Hide panel' : 'Show panel'}
            >
              {showRightPanel ? <ChevronRight /> : <ChevronLeft />}
            </div>

            {showRightPanel && (
              <div className="overflow-auto" style={{ maxHeight: '84vh' }}>
                <div className="mb-3">
                  <JeeSummaryComponent />
                </div>
                <JeeNavigationComponent />
              </div>
            )}
          </Col>
        </Row>
      </div>
    </>
  );
};

export default memo(JeeQuestionHolderComponent);
